import { z } from 'zod';

import { collectionPath, defineModelSpec } from '@elara/firebolt/shared';

import { FlightSegmentSchema, GoogleFlightBookingOptionSchema } from './schemas/flight-schemas.js';
import { ItineraryItemEventTypeSchema } from './schemas/itinerary-item-event-schemas.js';
import { BookingStatusSchema } from './schemas/itinerary-item-schemas.js';
import { RoomRatesSchema } from './schemas/room-schemas.js';
import { CostSchema } from './schemas/shared-schemas.js';
import { id } from './schemas/zod-types.js';

/**
 * Virtual (non-Firestore) specs for transient data models. These specs exist to
 * provide schema validation and type parity with Firestore-backed models when
 * used with local-only model factories.
 */
export const RoomRateModelSpec = defineModelSpec({
  path: collectionPath`virtual/roomRates/${id}`,
  shape: RoomRatesSchema.shape,
});

export const FlightSegmentModelSpec = defineModelSpec({
  path: collectionPath`virtual/flightSegments/${id}`,
  shape: FlightSegmentSchema.shape,
});

export const GoogleFlightBookingOptionModelSpec = defineModelSpec({
  path: collectionPath`virtual/googleBookingOptions/${id}`,
  shape: { id: z.string().optional(), ...GoogleFlightBookingOptionSchema.shape },
});

/**
 * Shape for itinerary item events (ephemeral, expanded from ItineraryItem documents).
 * Accepts all event variants; metadata shape varies by type.
 */
const ItineraryItemEventShape = {
  id: z.string(),
  title: z.string(),
  dateTime: z.string(),
  itemId: z.string(),
  type: ItineraryItemEventTypeSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
  cost: CostSchema.optional(),
  bookingStatus: BookingStatusSchema.optional(),
} as const;

export const ItineraryItemEventModelSpec = defineModelSpec({
  path: collectionPath`virtual/itineraryItemEvents/${id}`,
  shape: ItineraryItemEventShape,
});

export type RoomRateModelSpec = typeof RoomRateModelSpec;
export type FlightSegmentModelSpec = typeof FlightSegmentModelSpec;
export type GoogleFlightBookingOptionModelSpec = typeof GoogleFlightBookingOptionModelSpec;
export type ItineraryItemEventModelSpec = typeof ItineraryItemEventModelSpec;
